import React, { useEffect, useState } from "react";
import { UserCog, Plus, Pencil, Trash2, ShieldCheck, PencilLine, Eye } from "lucide-react";
import { PageHeader, SectionCard, Footer } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", cls: "bg-amber-100 text-amber-700", icon: ShieldCheck, desc: "Kendali penuh semua fitur" },
  editor: { label: "Editor", cls: "bg-blue-100 text-blue-700", icon: PencilLine, desc: "Input & edit data semua menu" },
  viewer: { label: "Viewer", cls: "bg-slate-100 text-slate-600", icon: Eye, desc: "Baca semua menu (kecuali Absensi), boleh input Finding" },
};

const emptyForm = { username: "", name: "", role: "viewer", password: "" };

export default function KelolaAkun() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [delTarget, setDelTarget] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => { const r = await api.get("/users"); setUsers(r.data); };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ username: u.username, name: u.name || "", role: u.role, password: "" }); setFormOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { username: form.username, name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.user_id}`, payload);
        toast.success("Akun diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("Akun dibuat");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan akun");
    }
  };

  const doDelete = async () => {
    const u = delTarget; setDelTarget(null);
    try { await api.delete(`/users/${u.user_id}`); toast.success("Akun dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus akun"); }
  };

  return (
    <div className="p-4 md:p-6">
      <PageHeader icon={UserCog} title="KELOLA AKUN" subtitle="Buat & atur akun Admin, Editor, dan Viewer beserta username/password"
        right={<Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-9" onClick={openCreate} data-testid="ka-add"><Plus className="w-4 h-4 mr-1" />Tambah Pengguna</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        {Object.entries(ROLE_META).map(([k, m]) => (
          <div key={k} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${m.cls}`}><m.icon className="w-5 h-5" /></div>
            <div><p className="font-bold text-slate-800">{m.label}</p><p className="text-xs text-slate-500">{m.desc}</p></div>
          </div>
        ))}
      </div>

      <SectionCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead><tr className="text-left text-slate-500 border-b text-xs"><th className="py-2 px-3">No</th><th className="px-3">Username</th><th className="px-3">Nama</th><th className="px-3">Role</th><th className="px-3 text-center">Aksi</th></tr></thead>
            <tbody>
              {users.map((u, i) => {
                const m = ROLE_META[u.role] || ROLE_META.viewer;
                return (
                  <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                    <td className="py-3 px-3">{i + 1}</td>
                    <td className="px-3 font-medium">{u.username}{u.user_id === user?.user_id && <span className="ml-2 text-[10px] text-slate-400">(Anda)</span>}</td>
                    <td className="px-3 text-slate-500">{u.name || "-"}</td>
                    <td className="px-3"><span className={`text-xs px-2 py-0.5 rounded font-medium ${m.cls}`}>{m.label}</span></td>
                    <td className="px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`user-edit-${u.username}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDelTarget(u)} disabled={u.user_id === user?.user_id} className="p-1.5 rounded hover:bg-red-50 text-red-500 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`user-del-${u.username}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">Belum ada pengguna</td></tr>}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle>
            <DialogDescription>{editing ? "Ubah data akun. Kosongkan password bila tidak ingin mengubahnya." : "Tentukan username, password, dan peran untuk akun baru."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Username</Label><Input value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="ka-username" /></div>
            <div><Label className="text-xs">Nama Lengkap</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ka-name" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="ka-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Password {editing && <span className="text-slate-400">(opsional saat edit)</span>}</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={editing ? "Biarkan kosong = tidak diubah" : ""} data-testid="ka-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="ka-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus akun {delTarget?.username}?</AlertDialogTitle>
            <AlertDialogDescription>Pengguna ini tidak akan bisa login lagi. Aksi tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="ka-del-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}
